/**
 * Image Optimization Utilities
 * 
 * Client-side image optimization for menu items, logos, and other uploads.
 * Converts images to WebP format, resizes them, and generates thumbnails.
 * 
 * Benefits:
 * - Reduces file size by 25-35% with WebP
 * - Faster uploads and downloads
 * - Better mobile performance
 * - Smaller storage costs
 */

// Configuration constants
const CONFIG = {
  MAX_WIDTH: 1200,           // Max width for full-size images
  THUMBNAIL_WIDTH: 300,      // Width for thumbnails
  QUALITY: 0.85,             // WebP quality (0-1)
  THUMBNAIL_QUALITY: 0.8,    // Thumbnail quality
  MAX_FILE_SIZE_MB: 5,       // Max file size before optimization
} as const;

export interface OptimizedImage {
  original: Blob;
  thumbnail: Blob;
  originalSize: number;
  optimizedSize: number;
  thumbnailSize: number;
  compressionRatio: number;
  width: number;
  height: number;
  format: string;
}

export interface OptimizationOptions {
  maxWidth?: number;
  thumbnailWidth?: number;
  quality?: number;
  thumbnailQuality?: number;
  generateThumbnail?: boolean;
}

/**
 * Check if the browser supports WebP encoding
 */
export function supportsWebP(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
}

/**
 * Get the output format based on browser support
 */
function getOutputFormat(): string {
  return supportsWebP() ? 'image/webp' : 'image/jpeg';
}

/**
 * Load an image file and return an ImageBitmap
 */
async function loadImage(file: File): Promise<ImageBitmap> {
  return createImageBitmap(file);
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number
): { width: number; height: number } {
  if (originalWidth <= maxWidth) {
    return { width: originalWidth, height: originalHeight };
  }
  
  const aspectRatio = originalHeight / originalWidth;
  return {
    width: maxWidth,
    height: Math.round(maxWidth * aspectRatio)
  };
}

/**
 * Resize and compress an image using OffscreenCanvas (if available) or regular Canvas
 */
async function resizeImage(
  img: ImageBitmap,
  targetWidth: number,
  targetHeight: number,
  quality: number
): Promise<Blob> {
  const format = getOutputFormat();
  
  // Try OffscreenCanvas first (better performance, works in workers)
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(targetWidth, targetHeight);
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Failed to get 2D context from OffscreenCanvas');
    }
    
    // Enable image smoothing for better quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    
    return canvas.convertToBlob({ type: format, quality });
  }
  
  // Fallback to regular Canvas
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Failed to get 2D context from Canvas');
  }
  
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      },
      format,
      quality
    );
  });
}

/**
 * Optimize an image file - resize, compress, and optionally generate thumbnail
 * 
 * @param file - The image file to optimize
 * @param options - Optimization options
 * @returns Promise<OptimizedImage> - The optimized image data
 * 
 * @example
 * ```typescript
 * const file = inputElement.files[0];
 * const result = await optimizeImage(file);
 * 
 * // Upload optimized version
 * await uploadToStorage(result.original, 'menu-images/item.webp');
 * await uploadToStorage(result.thumbnail, 'menu-images/item-thumb.webp');
 * 
 * console.log(`Compression: ${result.compressionRatio}%`);
 * ```
 */
export async function optimizeImage(
  file: File,
  options: OptimizationOptions = {}
): Promise<OptimizedImage> {
  const {
    maxWidth = CONFIG.MAX_WIDTH,
    thumbnailWidth = CONFIG.THUMBNAIL_WIDTH,
    quality = CONFIG.QUALITY,
    thumbnailQuality = CONFIG.THUMBNAIL_QUALITY,
    generateThumbnail = true
  } = options;
  
  const originalSize = file.size;
  
  // Load the image
  const img = await loadImage(file);
  const originalWidth = img.width;
  const originalHeight = img.height;
  
  // Calculate optimized dimensions
  const { width, height } = calculateDimensions(originalWidth, originalHeight, maxWidth);
  
  // Create optimized version
  const optimizedBlob = await resizeImage(img, width, height, quality);
  
  // Create thumbnail if requested
  let thumbnailBlob: Blob;
  if (generateThumbnail) {
    const thumbDimensions = calculateDimensions(originalWidth, originalHeight, thumbnailWidth);
    thumbnailBlob = await resizeImage(
      img,
      thumbDimensions.width,
      thumbDimensions.height,
      thumbnailQuality
    );
  } else {
    // Return a minimal placeholder if thumbnail not needed
    thumbnailBlob = optimizedBlob;
  }
  
  // Close the ImageBitmap to free memory
  img.close();
  
  const compressionRatio = Math.round((1 - optimizedBlob.size / originalSize) * 100);
  
  return {
    original: optimizedBlob,
    thumbnail: thumbnailBlob,
    originalSize,
    optimizedSize: optimizedBlob.size,
    thumbnailSize: thumbnailBlob.size,
    compressionRatio: Math.max(0, compressionRatio), // Don't show negative (rare case of larger output)
    width,
    height,
    format: getOutputFormat()
  };
}

/**
 * Quick optimization for a single image without thumbnail
 * Useful for logos or icons
 */
export async function optimizeImageQuick(
  file: File,
  maxWidth: number = 800,
  quality: number = 0.85
): Promise<Blob> {
  const result = await optimizeImage(file, {
    maxWidth,
    quality,
    generateThumbnail: false
  });
  return result.original;
}

/**
 * Check if an image needs optimization based on size and dimensions
 */
export async function needsOptimization(file: File): Promise<boolean> {
  // Always optimize if file is large
  if (file.size > CONFIG.MAX_FILE_SIZE_MB * 1024 * 1024) {
    return true;
  }
  
  // Check dimensions
  try {
    const img = await loadImage(file);
    const needsResize = img.width > CONFIG.MAX_WIDTH;
    img.close();
    return needsResize;
  } catch {
    // If we can't read the image, don't optimize
    return false;
  }
}

/**
 * Get a data URL preview of an optimized image
 * Useful for showing preview before upload
 */
export async function getPreviewUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read blob as data URL'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert a Blob to a File object (needed for some upload APIs)
 */
export function blobToFile(blob: Blob, filename: string): File {
  const extension = blob.type === 'image/webp' ? '.webp' : '.jpg';
  const finalFilename = filename.replace(/\.[^.]+$/, '') + extension;
  
  return new File([blob], finalFilename, { type: blob.type });
}

/**
 * Batch optimize multiple images
 * Processes images in parallel for better performance
 */
export async function optimizeImages(
  files: File[],
  options: OptimizationOptions = {}
): Promise<OptimizedImage[]> {
  return Promise.all(files.map(file => optimizeImage(file, options)));
}

// ============================================================================
// SERVER-SIDE / CDN IMAGE OPTIMIZATION (Supabase Image Transformations)
// ============================================================================

/**
 * Standard widths for responsive images
 * Covers common device breakpoints
 */
export const RESPONSIVE_WIDTHS = [320, 480, 640, 768, 1024, 1280] as const;

/**
 * Quality presets for different use cases
 */
export const QUALITY_PRESETS = {
  placeholder: 30, // Tiny blur placeholder
  low: 60,         // Thumbnail / list views
  medium: 75,      // Mobile / default
  high: 85,        // Desktop / detail views
  original: 100,   // Full quality
} as const;

export type QualityPreset = keyof typeof QUALITY_PRESETS;

export interface CdnImageOptions {
  width?: number;
  height?: number;
  quality?: QualityPreset | number;
  format?: 'webp' | 'avif' | 'jpeg' | 'png' | 'origin';
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Check if a URL is from Supabase storage
 * Used to determine if we can apply transformations
 */
export function isSupabaseStorageUrl(url: string): boolean {
  if (!url) return false;
  return url.includes('.supabase.co/storage/') || url.includes('supabase.co/storage/');
}

/**
 * Generate an optimized image URL using Supabase Image Transformation
 * 
 * Transforms the storage URL to use Supabase's image CDN with resizing,
 * format conversion (WebP), and quality optimization.
 * 
 * @example
 * // Original: https://xxx.supabase.co/storage/v1/object/public/images/photo.jpg
 * // Optimized: https://xxx.supabase.co/storage/v1/render/image/public/images/photo.jpg?width=640&quality=75&format=webp
 */
export function getOptimizedImageUrl(
  originalUrl: string,
  options: CdnImageOptions = {}
): string {
  if (!originalUrl) return '';
  
  // Only transform Supabase storage URLs
  if (!isSupabaseStorageUrl(originalUrl)) {
    return originalUrl;
  }

  const { 
    width, 
    height, 
    quality = 'medium', 
    format = 'webp', 
    resize = 'cover' 
  } = options;
  
  // Convert quality preset to number
  const qualityValue = typeof quality === 'number' 
    ? quality 
    : QUALITY_PRESETS[quality];

  // Transform URL: /object/public/ -> /render/image/public/
  const transformedUrl = originalUrl.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );

  // Build query params
  const params = new URLSearchParams();
  
  if (width) params.set('width', width.toString());
  if (height) params.set('height', height.toString());
  params.set('quality', qualityValue.toString());
  params.set('format', format);
  params.set('resize', resize);

  return `${transformedUrl}?${params.toString()}`;
}

/**
 * Generate responsive srcset for an image
 * Creates multiple sizes for responsive loading
 * 
 * @example
 * generateSrcSet(url) 
 * // Returns: "url?width=320 320w, url?width=480 480w, ..."
 */
export function generateSrcSet(
  originalUrl: string,
  options: { maxWidth?: number; format?: 'webp' | 'jpeg' } = {}
): string {
  if (!originalUrl || !isSupabaseStorageUrl(originalUrl)) {
    return '';
  }

  const { maxWidth = 1280, format = 'webp' } = options;
  
  const widths = RESPONSIVE_WIDTHS.filter(w => w <= maxWidth);
  
  return widths
    .map(width => {
      const optimizedUrl = getOptimizedImageUrl(originalUrl, {
        width,
        format,
        quality: width <= 480 ? 'medium' : 'high',
      });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
}

/**
 * Get sizes attribute for responsive images
 * Common patterns for restaurant menu layouts
 */
export function getImageSizes(
  layout: 'card' | 'hero' | 'thumbnail' | 'full' | 'grid'
): string {
  switch (layout) {
    case 'thumbnail':
      return '80px';
    case 'card':
      return '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
    case 'grid':
      return '(max-width: 480px) 50vw, (max-width: 768px) 33vw, 25vw';
    case 'hero':
    case 'full':
      return '100vw';
    default:
      return '(max-width: 640px) 100vw, 50vw';
  }
}

/**
 * Generate a tiny blur placeholder URL
 * Uses extremely low resolution (20px) with blur for fast loading
 */
export function getBlurPlaceholder(originalUrl: string): string {
  if (!originalUrl || !isSupabaseStorageUrl(originalUrl)) {
    return '';
  }

  return getOptimizedImageUrl(originalUrl, {
    width: 20,
    quality: 'placeholder',
    format: 'webp',
  });
}

/**
 * Get all optimized image props for use with img or OptimizedImage component
 * 
 * @example
 * const props = getResponsiveImageProps(imageUrl, { layout: 'card' });
 * return <img {...props} alt="Menu item" />;
 */
export function getResponsiveImageProps(
  originalUrl: string,
  options: {
    layout?: 'card' | 'hero' | 'thumbnail' | 'full' | 'grid';
    width?: number;
    priority?: boolean;
  } = {}
) {
  const { layout = 'card', width, priority = false } = options;
  
  // Get appropriate width based on layout
  const targetWidth = width || (
    layout === 'thumbnail' ? 160 : 
    layout === 'hero' ? 1280 : 
    layout === 'grid' ? 400 :
    640
  );
  
  const optimizedSrc = getOptimizedImageUrl(originalUrl, {
    width: targetWidth,
    format: 'webp',
    quality: priority ? 'high' : 'medium',
  });

  return {
    src: optimizedSrc,
    srcSet: generateSrcSet(originalUrl, { maxWidth: targetWidth * 2 }),
    sizes: getImageSizes(layout),
    loading: priority ? 'eager' as const : 'lazy' as const,
    decoding: priority ? 'sync' as const : 'async' as const,
    fetchpriority: priority ? 'high' as const : 'auto' as const,
  };
}

/**
 * Preload an image for LCP optimization
 * Call this for above-fold hero/cover images
 */
export function preloadCriticalImage(
  url: string, 
  options: CdnImageOptions = {}
): void {
  if (typeof window === 'undefined') return;
  
  const optimizedUrl = getOptimizedImageUrl(url, {
    width: 1280,
    quality: 'high',
    format: 'webp',
    ...options,
  });
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = optimizedUrl;
  link.type = 'image/webp';
  
  // Add fetchpriority for critical images (experimental but well-supported)
  // Using type assertion for fetchpriority which isn't in all TS lib versions
  (link as HTMLLinkElement & { fetchpriority?: string }).fetchpriority = 'high';
  
  document.head.appendChild(link);
}

/**
 * Get optimal dimensions for a given container width
 * Prevents layout shift by calculating dimensions upfront
 */
export function getOptimalDimensions(
  originalWidth: number,
  originalHeight: number,
  containerWidth: number,
  maxHeight?: number
): { width: number; height: number; aspectRatio: string } {
  const aspectRatio = originalWidth / originalHeight;
  
  let width = Math.min(originalWidth, containerWidth);
  let height = Math.round(width / aspectRatio);
  
  if (maxHeight && height > maxHeight) {
    height = maxHeight;
    width = Math.round(height * aspectRatio);
  }
  
  return { 
    width, 
    height,
    aspectRatio: `${originalWidth}/${originalHeight}`,
  };
}
